import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminSidebar from '@/components/AdminSidebar';
import { Plus, Pencil, Trash2, X, Check, Users } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const empty = { nama: '', guru_id: '', keterangan: '' };

export default function ManajemenHalaqah() {
  const [halaqah, setHalaqah] = useState([]);
  const [guru, setGuru] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/halaqah`).then(r => setHalaqah(r.data)).catch(() => {}),
      axios.get(`${API}/guru`).then(r => setGuru(r.data)).catch(() => {}),
    ]);
  }, []);

  const refetch = () => axios.get(`${API}/halaqah`).then(r => setHalaqah(r.data)).catch(() => {});

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) { await axios.put(`${API}/halaqah/${editId}`, form); toast.success('Halaqah diperbarui'); }
      else { await axios.post(`${API}/halaqah`, form); toast.success('Halaqah dibuat'); }
      setShowForm(false); setEditId(null); setForm(empty); refetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Gagal'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus halaqah ini?')) return;
    await axios.delete(`${API}/halaqah/${id}`);
    toast.success('Halaqah dihapus'); refetch();
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content bg-gray-50 min-h-screen">
        <div className="bg-white border-b border-gray-100 px-4 md:px-8 py-4 md:py-6 flex items-start md:items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="font-playfair text-2xl font-bold text-emerald-950">Manajemen Halaqah</h1>
            <p className="text-sm text-gray-500">{halaqah.length} halaqah aktif</p>
          </div>
          <button data-testid="add-halaqah-btn" onClick={() => { setForm(empty); setEditId(null); setShowForm(true); }}
            className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2.5 rounded-xl text-sm font-semibold shadow-emerald transition-all">
            <Plus size={16} /> Tambah Halaqah
          </button>
        </div>
        <div className="p-4 md:p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {halaqah.map((h, i) => {
            const g = guru.find(gu => gu.id === h.guru_id);
            return (
              <motion.div key={h.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-2xl p-6 shadow-card border border-gray-50">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center">
                    <span className="text-white font-bold">{h.nama?.charAt(0)}</span>
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => { setForm({ nama: h.nama, guru_id: h.guru_id||'', keterangan: h.keterangan||'' }); setEditId(h.id); setShowForm(true); }}
                      className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"><Pencil size={14} /></button>
                    <button onClick={() => handleDelete(h.id)} className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={14} /></button>
                  </div>
                </div>
                <h3 className="font-semibold text-gray-800">{h.nama}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{g ? `Guru: ${g.name}` : 'Belum ada guru'}</p>
                <div className="flex items-center gap-2 mt-3 text-xs text-emerald-600 bg-emerald-50 rounded-lg px-3 py-1.5 w-fit">
                  <Users size={12} />
                  <span>{h.santri_count || 0} santri</span>
                </div>
                {h.keterangan && <p className="text-xs text-gray-400 mt-2">{h.keterangan}</p>}
              </motion.div>
            );
          })}
          {halaqah.length === 0 && (
            <div className="col-span-3 text-center py-16 text-gray-400 text-sm">Belum ada halaqah</div>
          )}
        </div>

        <AnimatePresence>
          {showForm && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4"
              onClick={e => e.target === e.currentTarget && setShowForm(false)}>
              <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                className="bg-white rounded-2xl shadow-xl w-full max-w-md">
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                  <h2 className="font-playfair text-xl font-bold text-emerald-950">{editId ? 'Edit Halaqah' : 'Tambah Halaqah'}</h2>
                  <button onClick={() => setShowForm(false)} className="p-2 hover:bg-gray-100 rounded-xl"><X size={18} /></button>
                </div>
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nama Halaqah *</label>
                    <input required value={form.nama} onChange={e => setForm(f => ({...f, nama: e.target.value}))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Guru / Ustaz</label>
                    <select value={form.guru_id} onChange={e => setForm(f => ({...f, guru_id: e.target.value}))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
                      <option value="">-- Pilih Guru --</option>
                      {guru.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Keterangan</label>
                    <textarea value={form.keterangan} onChange={e => setForm(f => ({...f, keterangan: e.target.value}))}
                      rows={2} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 resize-none" />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button type="button" onClick={() => setShowForm(false)}
                      className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600">Batal</button>
                    <button data-testid="halaqah-form-submit" type="submit"
                      className="flex-1 py-2.5 bg-emerald-500 text-white rounded-xl text-sm font-semibold shadow-emerald flex items-center justify-center gap-2">
                      <Check size={15} /> Simpan
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
